/**
 * Delete Agent Modal
 *
 * 2026-06-09: scope change — deleting a support agent now routes their
 * open work to Unassigned instead of transferring to another agent.
 * Operator decision codified in
 * docs/AUDIT_PROMPT_SUPPORT_TASK_COUNTS_AGENT_DELETE_MEMBER_SEARCH_ZUORA_EMAIL_CHANGE.md
 * and migration 2026-06-09-003-soft-delete-support-agent-to-unassigned.sql.
 *
 * Calls the SECURITY DEFINER RPC soft_delete_support_agent_to_unassigned
 * which:
 *   - support_tasks.agent_id      → NULL  (open work only)
 *   - support_tasks.escalated_to  → NULL  (open work only)
 *   - contacts.assigned_agent_id  → NULL  (portfolios)
 *   - support_agents.supervisor_id (subordinates) → NULL
 *   - source agent → is_active = false
 *
 * Completed and cancelled tasks keep their historical attribution.
 * The previous "transfer to target agent" flow is still available via
 * softDeleteAgentAndReassign for callers that need explicit transfer.
 */

import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  softDeleteAgentToUnassigned,
  type SoftDeleteAgentToUnassignedResult,
} from "../../services/supportTasksDb.service";
import type { UUID } from "../../types/common.types";

interface DeleteAgentModalProps {
  agent: { id: UUID; name: string };
  // Kept on the props for API stability with the caller, but no longer
  // consumed since we no longer pick a target.
  allAgents?: Array<{ id: UUID; name: string; email: string }>;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteAgentModal: React.FC<DeleteAgentModalProps> = ({
  agent,
  onClose,
  onSuccess,
}) => {
  const [error, setError] = useState<string>("");
  const [result, setResult] =
    useState<SoftDeleteAgentToUnassignedResult | null>(null);
  const [confirmText, setConfirmText] = useState<string>("");

  const deleteMutation = useMutation({
    mutationFn: ({ agentId }: { agentId: UUID }) =>
      softDeleteAgentToUnassigned(agentId),
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (err: Error) => {
      const msg = err.message || "Failed to delete agent";
      if (msg.includes("already inactive")) {
        setError("This agent was already deleted by another action.");
      } else {
        setError(msg);
      }
    },
  });

  const handleConfirm = () => {
    setError("");
    deleteMutation.mutate({ agentId: agent.id });
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              Agent deactivated
            </h2>
          </div>
          <div className="p-6 space-y-3 text-sm text-gray-700">
            <p>
              <strong>{result.agent_name}</strong> deactivated and their open
              work was routed to <strong>Unassigned</strong>.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              <li>Open tasks moved to Unassigned: {result.tasks_unassigned}</li>
              <li>Open escalations cleared: {result.escalations_cleared}</li>
              <li>Contact assignments cleared: {result.contacts_unassigned}</li>
              <li>
                Supervisor links cleared: {result.supervisor_links_cleared}
              </li>
            </ul>
            <p className="text-xs text-gray-500 mt-2">
              Anyone with the right role can pick these tasks back up from the
              Unassigned tab.
            </p>
          </div>
          <div className="px-6 py-4 border-t flex justify-end">
            <button
              onClick={onSuccess}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  const confirmed = confirmText.trim().toLowerCase() === "delete";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Delete Agent</h2>
          <p className="text-sm text-gray-500 mt-1">
            Deactivate <strong>{agent.name}</strong>. Their open work will be
            moved to <strong>Unassigned</strong>.
          </p>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="text-xs text-gray-600 space-y-1">
            <p>
              This action sets <strong>{agent.name}</strong>'s status to
              inactive and:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Moves open pending / in-progress tasks they own to the
                Unassigned bucket.
              </li>
              <li>Clears open escalations that point at them.</li>
              <li>Removes them as the assigned agent on any contacts.</li>
              <li>
                Clears them as supervisor on any other agents (so you can
                manually re-assign supervision).
              </li>
              <li>
                Leaves completed and cancelled tasks with their original
                attribution for history.
              </li>
            </ul>
          </div>

          <div>
            <label
              htmlFor="confirm-delete"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Type{" "}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">delete</code>{" "}
              to confirm
            </label>
            <input
              id="confirm-delete"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!confirmed || deleteMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {deleteMutation.isPending
              ? "Deactivating…"
              : "Deactivate & Route to Unassigned"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAgentModal;
